import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, X, ArrowRight, Shield, Zap, Star, Loader } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../App';
import { SubscriptionPlan } from '../services/subscriptionService';
import { redirectToCheckout, STRIPE_PRICE_IDS } from '../services/stripeService';

interface PricingTier {
  name: string;
  price: string;
  description: string;
  features: string[];
  limit: number;
  highlight?: boolean;
  ctaText: string;
  icon: React.ReactNode;
}

export const PricingPage: React.FC = () => {
  const { t } = useLanguage();
  const { session } = useAuth();
  
  const [isLoading, setIsLoading] = useState<SubscriptionPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  
  // Translation helper for consistent handling
  const getTranslation = (key: string, defaultText: string) => {
    try {
      // @ts-ignore - Ignore type checking for translation keys
      return t(key) || defaultText;
    } catch {
      return defaultText;
    }
  };

  const handleSubscribe = async (planType: SubscriptionPlan) => {
    if (!session || !session.user) {
      navigate('/auth?redirect=/pricing');
      return;
    }

    if (planType === SubscriptionPlan.FREE) {
      navigate('/app');
      return;
    }

    setIsLoading(planType);
    setError(null);

    try {
      const supabaseUserId = session.user.id;
      const userEmail = session.user.email;
      console.log('User email for checkout:', userEmail);
      console.log('Supabase User ID for checkout:', supabaseUserId);
      
      await redirectToCheckout(planType, supabaseUserId, userEmail);
    } catch (err) {
      console.error('Error redirecting to checkout:', err);
      setError('Failed to process subscription. Please try again later.');
      setIsLoading(null);
    }
  };

  const tiers: PricingTier[] = [
    {
      name: 'Free',
      price: '$0',
      description: 'For individuals and small teams testing our platform',
      limit: 30,
      features: [
        'Up to 30 resume analyses monthly',
        'Basic skills matching',
        'Core qualification assessment',
        'Simple candidate ranking',
        'Single user access',
        'Email support'
      ],
      ctaText: session ? 'Current Plan' : 'Get Started',
      icon: <Shield className="h-6 w-6 text-blue-500" />
    },
    {
      name: 'Standard',
      price: '$20',
      description: 'Ideal for recruiters and HR professionals',
      limit: 500,
      highlight: true,
      features: [
        'Up to 500 resume analyses monthly',
        'Advanced skills & experience matching',
        'Detailed qualification assessment',
        'Candidate strength & gap analysis',
        'PDF report generation',
        'Bulk resume processing',
        'Priority support response'
      ],
      ctaText: 'Upgrade Now',
      icon: <Zap className="h-6 w-6 text-indigo-500" />
    },
    {
      name: 'Premium',
      price: '$50',
      description: 'Complete solution for recruitment teams',
      limit: 1500,
      features: [
        'Up to 1,500 resume analyses monthly',
        'AI-powered custom matching algorithms',
        'Comprehensive qualification scoring',
        'Team collaboration features',
        'API access for integrations',
        'Custom reporting dashboard',
        'Dedicated account manager',
        'Data retention controls'
      ],
      ctaText: 'Upgrade to Premium',
      icon: <Star className="h-6 w-6 text-purple-500" />
    }
  ];

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white py-20">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-4xl font-extrabold mb-4 text-gray-900">
            Choose Your Recruitment Edge
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Scale your candidate screening with the plan that fits your hiring volume
          </p>
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-indigo-100 text-indigo-800">
            <span className="text-sm font-medium">
              All plans include our core resume parsing and AI-matching technology
            </span>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div className="max-w-3xl mx-auto mb-8">
            <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded relative">
              <span className="block sm:inline">{error}</span>
              <button 
                className="absolute top-0 bottom-0 right-0 px-4 py-3"
                onClick={() => setError(null)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Pricing Tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {tiers.map((tier) => (
            <div 
              key={tier.name}
              className={`rounded-2xl overflow-hidden transition-all duration-300 transform hover:shadow-xl hover:-translate-y-1 ${
                tier.highlight 
                  ? 'bg-white border-2 border-indigo-500 shadow-lg relative' 
                  : 'bg-white border border-gray-200 shadow'
              }`}
            >
              {tier.highlight && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <span className="px-4 py-1 bg-indigo-500 text-white text-sm font-bold rounded-full shadow-lg">
                    Most Popular
                  </span>
                </div>
              )}
              
              <div className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  {tier.icon}
                  <h3 className="text-2xl font-bold text-gray-900">{tier.name}</h3>
                </div>
                <div className="mb-6">
                  <span className="text-4xl font-extrabold text-gray-900">{tier.price}</span>
                  <span className="text-gray-600 ml-2">/ month</span>
                </div>
                <p className="text-gray-600 mb-6">{tier.description}</p>
                <div className="border-t border-gray-100 pt-6 mb-6">
                  <p className="font-medium text-gray-900 mb-4">What's included:</p>
                  <ul className="space-y-3">
                    {tier.features.map((feature) => (
                      <li key={feature} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <button
                  onClick={() => handleSubscribe(
                    tier.name === 'Free' 
                      ? SubscriptionPlan.FREE 
                      : tier.name === 'Standard' 
                        ? SubscriptionPlan.STANDARD 
                        : SubscriptionPlan.PREMIUM
                  )}
                  disabled={isLoading !== null || (tier.name === 'Free' && !!session)}
                  className={`w-full py-3 px-4 rounded-full flex items-center justify-center font-medium transition-colors ${
                    isLoading === (tier.name === 'Free' 
                      ? SubscriptionPlan.FREE 
                      : tier.name === 'Standard' 
                        ? SubscriptionPlan.STANDARD 
                        : SubscriptionPlan.PREMIUM)
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : tier.highlight
                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                        : tier.name === 'Free' && !!session
                          ? 'bg-gray-100 text-gray-800 cursor-not-allowed'
                          : 'bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50'
                  }`}
                >
                  {isLoading === (tier.name === 'Free' 
                    ? SubscriptionPlan.FREE 
                    : tier.name === 'Standard' 
                      ? SubscriptionPlan.STANDARD 
                      : SubscriptionPlan.PREMIUM) ? (
                    <Loader className="animate-spin h-5 w-5 mr-2" />
                  ) : null}
                  {tier.ctaText}
                  {tier.name !== 'Free' || !session ? (
                    <ArrowRight className="ml-2 h-4 w-4" />
                  ) : null}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-24 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-10 text-gray-900">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                How do the monthly analysis limits work?
              </h3>
              <p className="text-gray-600">
                Your monthly limit resets on your billing date. Each resume processed counts as one analysis. You can view your current usage in your account dashboard at any time.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                What happens if I need more analyses than my plan allows?
              </h3>
              <p className="text-gray-600">
                You'll receive a notification when you reach 80% of your limit. You can upgrade your plan at any time, and the new limit will take effect immediately. Alternatively, you can wait until your next billing cycle.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                How accurate is the AI matching technology?
              </h3>
              <p className="text-gray-600">
                Our proprietary algorithms have been trained on millions of resumes and job descriptions, achieving 98% matching accuracy compared to human recruiters. All plans use the same core AI technology, with Premium offering additional customization options.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Can I try the platform before committing to a paid plan?
              </h3>
              <p className="text-gray-600">
                Yes! Our Free plan allows you to experience the core features with up to 10 resume analyses per month. Additionally, paid plans come with a 14-day money-back guarantee if you're not completely satisfied.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-24 bg-indigo-900 rounded-2xl p-10 text-center max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white mb-4">
            Reduce time-to-hire by up to 75%
          </h2>
          <p className="text-xl text-indigo-200 mb-8 max-w-2xl mx-auto">
            Join 1000+ companies leveraging our AI-powered resume analysis to find better candidates faster.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/app"
              className="px-8 py-4 bg-white text-indigo-900 font-semibold rounded-full hover:bg-gray-100 transition-colors shadow-lg"
            >
              Get Started Now
            </Link>
            <a
              href="#contact"
              className="px-8 py-4 bg-transparent text-white border-2 border-white font-semibold rounded-full hover:bg-white/10 transition-colors"
            >
              Contact Sales
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}; 